import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API } from '../api/api';
import { formatHours } from '../utils/events';
import { MANA_COST, bossStats, myProjectHours, pickBossProject } from '../utils/bossBattle';
import spellData from '../data/spells.json';
import bosses from '../data/bossSprites.json';
import { prefersReducedMotion } from './useProgressFx';

const SPELLS = spellData.spells;
const bossName = (monster) => bosses.monsters[monster]?.name || 'Boss';
const MONSTERS = 20;
const MUTATION_WINDOW_MS = 3000;
const AFTER_IMPACT_MS = 900;
const FLOAT_MS = 1350;
const BANNER_MS = 2600;

// Samme idé som serverens fallback: uden svar fra serveren får projektet stadig et fast monster.
const fallbackMonster = (projectId) => Number(String(projectId).slice(-4)) % MONSTERS || 0;

// Boss battle: valg af projekt, HP/mana, cast med optimistisk mana og fejringer. Tallene stammer fra Zoho
// (budget og actual via projects) og fra brugerens egne events; serveren husker monster og kastede timer.
export function useBossBattle({ events, projects, isOutlookSyncEvent, mutationAtRef }) {
  const picked = useMemo(
    () => pickBossProject({ events, projects, now: new Date(), isOutlookSyncEvent }),
    [events, projects, isOutlookSyncEvent],
  );
  const project = picked?.project || null;
  const projectId = project?.id || null;
  const myHours = useMemo(
    () => (projectId ? myProjectHours({ events, projectId, now: new Date(), isOutlookSyncEvent }) : 0),
    [events, projectId, isOutlookSyncEvent],
  );

  const [server, setServer] = useState({ projectId: null, monster: null, castHours: 0 });
  useEffect(() => {
    if (!projectId) return undefined;
    let annulleret = false;
    API.getBoss(projectId)
      .then((r) => { if (!annulleret) setServer({ projectId, monster: Number.isInteger(r?.monster) ? r.monster : fallbackMonster(projectId), castHours: Number(r?.castHours) || 0 }); })
      .catch(() => { if (!annulleret) setServer({ projectId, monster: fallbackMonster(projectId), castHours: 0 }); });
    return () => { annulleret = true; };
  }, [projectId]);

  const ready = !!project && server.projectId === projectId;
  const [castHours, setCastHours] = useState(0);
  useEffect(() => { if (ready) setCastHours(server.castHours); }, [ready, server]);

  const [casting, setCasting] = useState(false);
  const [reserved, setReserved] = useState(0);
  const [spell, setSpell] = useState(null);
  const [castKey, setCastKey] = useState(0);
  const [hitKey, setHitKey] = useState(0);
  const [dmg, setDmg] = useState(null);
  const [manaFloat, setManaFloat] = useState(null);
  const [justDefeated, setJustDefeated] = useState(false);
  const [banner, setBanner] = useState(null);
  const [burstKey, setBurstKey] = useState(0);
  const timers = useRef([]);
  const lastCastAt = useRef(0);
  const later = useCallback((fn, ms) => { timers.current.push(setTimeout(fn, ms)); }, []);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const stats = useMemo(
    () => (project ? bossStats({ project, myHours, castHours }) : null),
    [project, myHours, castHours],
  );
  const mana = stats ? Math.max(0, stats.mana - reserved) : 0;
  const spellsReady = Math.floor(mana / MANA_COST + 1e-9);
  const defeated = !!stats?.defeated;
  const canCast = ready && !casting && !defeated && spellsReady >= 1;

  const cast = useCallback(() => {
    if (!canCast) return;
    const chosen = SPELLS[Math.floor(Math.random() * SPELLS.length)];
    const dur = prefersReducedMotion() ? 0 : chosen.dur;
    lastCastAt.current = Date.now();
    setSpell(chosen);
    setCastKey((k) => k + 1);
    setCasting(true);
    setReserved(MANA_COST);
    const call = API.castSpell(projectId, myHours).then((r) => r, () => null);
    later(async () => {
      const r = await call;
      setReserved(0);
      if (r && Number.isFinite(r.castHours)) {
        setCastHours(r.castHours);
        setHitKey((k) => k + 1);
        setDmg({ id: Date.now(), text: `-${formatHours(MANA_COST)}` });
        later(() => setDmg(null), FLOAT_MS);
      }
    }, dur);
    later(() => setCasting(false), dur + AFTER_IMPACT_MS);
  }, [canCast, projectId, myHours, later]);

  // Besejret efter et cast: sænk bossen, vis banner og konfetti. Ved load vises den bare som besejret.
  const wasDefeated = useRef(null);
  useEffect(() => {
    if (!ready) return;
    if (wasDefeated.current === false && defeated && Date.now() - lastCastAt.current < 5000) {
      setJustDefeated(true);
      setBanner({ title: `Boss defeated · ${bossName(server.monster)}`, sub: `${formatHours(stats.budget)} delivered on budget` });
      setBurstKey((k) => k + 1);
      later(() => setBanner(null), BANNER_MS);
    }
    wasDefeated.current = defeated;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, defeated]);

  // Ny mana, når brugeren selv logger tid på boss-projektet (samme vindue som de andre fejringer).
  const prevHours = useRef(null);
  useEffect(() => {
    const before = prevHours.current;
    prevHours.current = ready ? { projectId, myHours } : null;
    if (!before || before.projectId !== projectId) return;
    const delta = myHours - before.myHours;
    if (delta > 0.001 && Date.now() - (mutationAtRef?.current || 0) < MUTATION_WINDOW_MS) {
      setManaFloat({ id: Date.now(), text: `+${formatHours(delta)} MP` });
      later(() => setManaFloat(null), FLOAT_MS);
    }
  }, [ready, projectId, myHours, mutationAtRef, later]);

  return {
    project, ready, monster: ready ? server.monster : null, stats, mana, spellsReady, defeated, justDefeated,
    casting, canCast, cast, spell, castKey, hitKey, dmg, manaFloat, banner, burstKey,
  };
}
